/**
 * ImmersiveThemeGallery
 * 
 * Galeria premium para visualização e seleção de temas imersivos.
 * Inclui preview interativo com exemplos de componentes temáticos.
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, 
  Check, 
  Eye, 
  Sparkles, 
  Layers, 
  Terminal, 
  Leaf, 
  Crown,
  X,
  Settings,
  Accessibility,
  Moon,
  Sun,
  Zap
} from 'lucide-react';
import { useImmersiveTheme, useThemePresets } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, THEME_METADATA } from '../../types/immersive-theme.types';
import { ThemedInput, ThemedButton, ThemedCard, ThemedBadge, ThemedSelect } from '../themed';

// ============================================
// TYPES
// ============================================

interface ThemeCardProps {
  type: ImmersiveThemeType;
  isActive: boolean;
  isPreview: boolean;
  onSelect: () => void;
  onPreview: () => void;
}

interface PreviewPanelProps {
  type: ImmersiveThemeType;
  onClose: () => void;
  onApply: () => void;
}

// ============================================
// THEME ICONS
// ============================================

const THEME_ICONS: Record<ImmersiveThemeType, React.ReactNode> = {
  [ImmersiveThemeType.NONE]: <Palette className="w-6 h-6" />,
  [ImmersiveThemeType.NEUMORPHISM]: <Layers className="w-6 h-6" />,
  [ImmersiveThemeType.BRUTALIST]: <Zap className="w-6 h-6" />,
  [ImmersiveThemeType.RETRO_TERMINAL]: <Terminal className="w-6 h-6" />,
  [ImmersiveThemeType.ORGANIC_NATURE]: <Leaf className="w-6 h-6" />,
  [ImmersiveThemeType.LUXURY_GOLD]: <Crown className="w-6 h-6" />,
};


// ============================================
// THEME PREVIEW BACKGROUNDS
// ============================================

const THEME_PREVIEW_STYLES: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    background: 'linear-gradient(145deg, #e6e9ef 0%, #d1d5db 100%)',
    boxShadow: 'inset 4px 4px 8px #c8ccd4, inset -4px -4px 8px #ffffff',
  },
  [ImmersiveThemeType.BRUTALIST]: {
    background: '#ffffff',
    border: '4px solid #000000',
  },
  [ImmersiveThemeType.RETRO_TERMINAL]: {
    background: 'linear-gradient(180deg, #0a0a0a 0%, #0d1a0d 100%)',
    boxShadow: 'inset 0 0 50px rgba(0, 255, 0, 0.1)',
  },
  [ImmersiveThemeType.ORGANIC_NATURE]: {
    background: 'linear-gradient(135deg, #f5f0e8 0%, #e8e0d5 100%)',
    borderRadius: '24px',
  },
  [ImmersiveThemeType.LUXURY_GOLD]: {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    border: '1px solid rgba(212, 175, 55, 0.3)',
  },
};

// ============================================
// THEME CARD COMPONENT
// ============================================

const ThemeCard: React.FC<ThemeCardProps> = ({
  type,
  isActive,
  isPreview,
  onSelect,
  onPreview,
}) => {
  const metadata = THEME_METADATA[type];
  const previewStyle = THEME_PREVIEW_STYLES[type];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: 1.02 }}
      className={`
        relative rounded-xl overflow-hidden cursor-pointer
        transition-all duration-300
        ${isActive ? 'ring-2 ring-primary-500 ring-offset-2' : ''}
        ${isPreview ? 'ring-2 ring-amber-500 ring-offset-2' : ''}
      `}
    >
      {/* Preview Area */}
      <div
        className="h-40 p-4 flex items-center justify-center"
        style={previewStyle}
      >
        <div className="text-center">
          <div className={`
            inline-flex items-center justify-center w-12 h-12 rounded-full mb-2
            ${type === ImmersiveThemeType.RETRO_TERMINAL ? 'text-green-400' : ''}
            ${type === ImmersiveThemeType.LUXURY_GOLD ? 'text-amber-400' : ''}
            ${type === ImmersiveThemeType.ORGANIC_NATURE ? 'text-emerald-600' : ''}
            ${type === ImmersiveThemeType.BRUTALIST ? 'text-black' : ''}
            ${type === ImmersiveThemeType.NEUMORPHISM ? 'text-gray-600' : ''}
            ${type === ImmersiveThemeType.NONE ? 'text-gray-500' : ''}
          `}>
            {THEME_ICONS[type]}
          </div>
          <p className={`
            text-sm font-medium
            ${type === ImmersiveThemeType.RETRO_TERMINAL ? 'text-green-400 font-mono' : ''}
            ${type === ImmersiveThemeType.LUXURY_GOLD ? 'text-amber-200' : ''}
            ${type === ImmersiveThemeType.ORGANIC_NATURE ? 'text-emerald-800' : ''}
            ${type === ImmersiveThemeType.BRUTALIST ? 'text-black font-black uppercase' : ''}
            ${type === ImmersiveThemeType.NEUMORPHISM ? 'text-gray-600' : ''}
            ${type === ImmersiveThemeType.NONE ? 'text-gray-600' : ''}
          `}>
            {metadata.name}
          </p>
        </div>
      </div>

      {/* Info Area */}
      <div className="p-4 bg-surface-800">
        <h3 className="font-semibold text-white mb-1">{metadata.name}</h3>
        <p className="text-sm text-surface-400 mb-3 line-clamp-2">
          {metadata.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {metadata.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs rounded-full bg-surface-700 text-surface-300"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg
                       bg-surface-700 hover:bg-surface-600 text-surface-300 text-sm
                       transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
            disabled={isActive}
            className={`
              flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm
              transition-colors
              ${isActive 
                ? 'bg-primary-600 text-white cursor-default' 
                : 'bg-primary-500 hover:bg-primary-400 text-white'}
            `}
          >
            {isActive ? (
              <>
                <Check className="w-4 h-4" />
                Ativo
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Aplicar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Active Badge */}
      {isActive && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-500 text-white">
            Ativo
          </span>
        </div>
      )}

      {/* Preview Badge */}
      {isPreview && !isActive && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-500 text-white">
            Preview
          </span>
        </div>
      )}
    </motion.div>
  );
};


// ============================================
// PREVIEW PANEL COMPONENT
// ============================================

const PreviewPanel: React.FC<PreviewPanelProps> = ({ type, onClose, onApply }) => {
  const metadata = THEME_METADATA[type];
  const [sampleInput, setSampleInput] = useState('');
  const [sampleSelect, setSampleSelect] = useState('');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-4xl max-h-[90vh] overflow-auto rounded-2xl bg-surface-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-surface-700 bg-surface-900">
          <div className="flex items-center gap-3">
            <div className={`
              p-2 rounded-lg
              ${type === ImmersiveThemeType.RETRO_TERMINAL ? 'bg-green-900/30 text-green-400' : ''}
              ${type === ImmersiveThemeType.LUXURY_GOLD ? 'bg-amber-900/30 text-amber-400' : ''}
              ${type === ImmersiveThemeType.ORGANIC_NATURE ? 'bg-emerald-900/30 text-emerald-400' : ''}
              ${type === ImmersiveThemeType.BRUTALIST ? 'bg-gray-800 text-white' : ''}
              ${type === ImmersiveThemeType.NEUMORPHISM ? 'bg-gray-700 text-gray-300' : ''}
              ${type === ImmersiveThemeType.NONE ? 'bg-gray-700 text-gray-300' : ''}
            `}>
              {THEME_ICONS[type]}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{metadata.name}</h2>
              <p className="text-sm text-surface-400">{metadata.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-700 text-surface-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview Content */}
        <div 
          className="p-6"
          style={THEME_PREVIEW_STYLES[type]}
          data-immersive-theme={type}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Input Examples */}
            <ThemedCard title="Inputs" hoverable>
              <div className="space-y-4">
                <ThemedInput
                  placeholder="Digite algo..."
                  value={sampleInput}
                  onChange={(e) => setSampleInput(e.target.value)}
                />
                <ThemedInput
                  placeholder="Campo desabilitado"
                  disabled
                />
                <ThemedInput
                  placeholder="Campo com erro"
                  error="Este campo é obrigatório"
                />
                <ThemedSelect
                  options={[
                    { value: 'opt1', label: 'Opção 1' },
                    { value: 'opt2', label: 'Opção 2' },
                    { value: 'opt3', label: 'Opção 3' },
                  ]}
                  value={sampleSelect}
                  onChange={setSampleSelect}
                  placeholder="Selecione uma opção"
                />
              </div>
            </ThemedCard>

            {/* Button Examples */}
            <ThemedCard title="Botões" hoverable>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <ThemedButton variant="primary">Primário</ThemedButton>
                  <ThemedButton variant="secondary">Secundário</ThemedButton>
                  <ThemedButton variant="ghost">Ghost</ThemedButton>
                  <ThemedButton variant="danger">Perigo</ThemedButton>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ThemedButton variant="primary" size="sm">Pequeno</ThemedButton>
                  <ThemedButton variant="primary" size="md">Médio</ThemedButton>
                  <ThemedButton variant="primary" size="lg">Grande</ThemedButton>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ThemedButton variant="primary" disabled>Desabilitado</ThemedButton>
                  <ThemedButton variant="primary" loading>Carregando</ThemedButton>
                </div>
              </div>
            </ThemedCard>

            {/* Badge Examples */}
            <ThemedCard title="Badges" hoverable>
              <div className="flex flex-wrap gap-2">
                <ThemedBadge variant="default">Default</ThemedBadge>
                <ThemedBadge variant="success">Sucesso</ThemedBadge>
                <ThemedBadge variant="warning">Aviso</ThemedBadge>
                <ThemedBadge variant="error">Erro</ThemedBadge>
                <ThemedBadge variant="info">Info</ThemedBadge>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <ThemedBadge size="sm">Pequeno</ThemedBadge>
                <ThemedBadge size="md">Médio</ThemedBadge>
                <ThemedBadge size="lg">Grande</ThemedBadge>
              </div>
            </ThemedCard>

            {/* Card Examples */}
            <ThemedCard 
              title="Card Exemplo" 
              subtitle="Subtítulo do card"
              footer={
                <div className="flex justify-end gap-2">
                  <ThemedButton variant="ghost" size="sm">Cancelar</ThemedButton>
                  <ThemedButton variant="primary" size="sm">Confirmar</ThemedButton>
                </div>
              }
              hoverable
            >
              <p className="text-sm">
                Este é um exemplo de card com header, conteúdo e footer.
                O estilo se adapta automaticamente ao tema selecionado.
              </p>
            </ThemedCard>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-between p-6 border-t border-surface-700 bg-surface-900">
          <div className="flex items-center gap-2 text-sm text-surface-400">
            <Accessibility className="w-4 h-4" />
            <span>WCAG AA Compliant</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
            >
              Fechar
            </button>
            <button
              onClick={onApply}
              className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-400 text-white transition-colors flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Aplicar Tema
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};


// ============================================
// ACCESSIBILITY SETTINGS PANEL
// ============================================

interface AccessibilityPanelProps {
  reducedMotion: boolean;
  highContrast: boolean;
  onReducedMotionChange: (value: boolean) => void;
  onHighContrastChange: (value: boolean) => void;
}

const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({
  reducedMotion,
  highContrast,
  onReducedMotionChange,
  onHighContrastChange,
}) => {
  return (
    <div className="p-4 rounded-xl bg-surface-800 border border-surface-700">
      <div className="flex items-center gap-2 mb-4">
        <Accessibility className="w-5 h-5 text-primary-400" />
        <h3 className="font-semibold text-white">Acessibilidade</h3>
      </div>

      <div className="space-y-4">
        {/* Reduced Motion */}
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-surface-700">
              <Zap className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Reduzir Animações</p>
              <p className="text-xs text-surface-400">Desabilita ou simplifica animações</p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={reducedMotion}
            onClick={() => onReducedMotionChange(!reducedMotion)}
            className={`
              relative w-11 h-6 rounded-full transition-colors
              ${reducedMotion ? 'bg-primary-500' : 'bg-surface-600'}
            `}
          >
            <span
              className={`
                absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white
                transition-transform
                ${reducedMotion ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </label>

        {/* High Contrast */}
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-surface-700">
              <Sun className="w-4 h-4 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Alto Contraste</p>
              <p className="text-xs text-surface-400">Aumenta o contraste das cores</p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={highContrast}
            onClick={() => onHighContrastChange(!highContrast)}
            className={`
              relative w-11 h-6 rounded-full transition-colors
              ${highContrast ? 'bg-primary-500' : 'bg-surface-600'}
            `}
          >
            <span
              className={`
                absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white
                transition-transform
                ${highContrast ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </label>
      </div>
    </div>
  );
};

// ============================================
// MAIN GALLERY COMPONENT
// ============================================

export const ImmersiveThemeGallery: React.FC = () => {
  const {
    theme: currentTheme,
    isPreviewMode,
    reducedMotion,
    highContrast,
    setTheme,
    previewTheme,
    applyPreview,
    cancelPreview,
    setAccessibility,
    isLoading,
  } = useImmersiveTheme();

  const { data: presets, isLoading: isLoadingPresets } = useThemePresets();
  const [previewType, setPreviewType] = useState<ImmersiveThemeType | null>(null);

  // Handlers
  const handleSelectTheme = useCallback(async (type: ImmersiveThemeType) => {
    try {
      await setTheme(type);
    } catch (error) {
      console.error('Erro ao aplicar tema:', error);
    }
  }, [setTheme]);

  const handlePreviewTheme = useCallback(async (type: ImmersiveThemeType) => {
    try {
      await previewTheme(type);
      setPreviewType(type);
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
    }
  }, [previewTheme]);

  const handleApplyPreview = useCallback(async () => {
    try {
      await applyPreview();
      setPreviewType(null);
    } catch (error) {
      console.error('Erro ao aplicar tema:', error);
    }
  }, [applyPreview]);

  const handleClosePreview = useCallback(() => {
    cancelPreview();
    setPreviewType(null);
  }, [cancelPreview]);

  const handleAccessibilityChange = useCallback(async (
    newReducedMotion: boolean,
    newHighContrast: boolean
  ) => {
    try {
      await setAccessibility(newReducedMotion, newHighContrast);
    } catch (error) {
      console.error('Erro ao atualizar configurações:', error);
    }
  }, [setAccessibility]);

  // Theme types to display (excluding NONE for the gallery)
  const themeTypes = Object.values(ImmersiveThemeType).filter(
    (t) => t !== ImmersiveThemeType.NONE
  );

  if (isLoadingPresets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Palette className="w-7 h-7 text-primary-400" />
            Temas Imersivos
          </h2>
          <p className="text-surface-400 mt-1">
            Transforme a aparência de toda a aplicação com temas visuais únicos
          </p>
        </div>

        {/* Reset Button */}
        {currentTheme !== ImmersiveThemeType.NONE && (
          <button
            onClick={() => handleSelectTheme(ImmersiveThemeType.NONE)}
            className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-surface-300 text-sm transition-colors"
          >
            Resetar para Padrão
          </button>
        )}
      </div>

      {/* Preview Mode Banner */}
      <AnimatePresence>
        {isPreviewMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center justify-between p-4 rounded-xl bg-amber-900/30 border border-amber-500/30"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-amber-400" />
              <div>
                <p className="font-medium text-amber-200">Modo Preview Ativo</p>
                <p className="text-sm text-amber-300/70">
                  Você está visualizando um tema. Aplique ou cancele para continuar.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleClosePreview}
                className="px-3 py-1.5 rounded-lg bg-surface-700 hover:bg-surface-600 text-white text-sm transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleApplyPreview}
                className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white text-sm transition-colors"
              >
                Aplicar
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Accessibility Settings */}
      <AccessibilityPanel
        reducedMotion={reducedMotion}
        highContrast={highContrast}
        onReducedMotionChange={(v) => handleAccessibilityChange(v, highContrast)}
        onHighContrastChange={(v) => handleAccessibilityChange(reducedMotion, v)}
      />

      {/* Theme Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {themeTypes.map((type) => (
            <ThemeCard
              key={type}
              type={type}
              isActive={currentTheme === type && !isPreviewMode}
              isPreview={isPreviewMode && previewType === type}
              onSelect={() => handleSelectTheme(type)}
              onPreview={() => handlePreviewTheme(type)}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewType && (
          <PreviewPanel
            type={previewType}
            onClose={handleClosePreview}
            onApply={handleApplyPreview}
          />
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default ImmersiveThemeGallery;
